import {
  ActionConfirmationModal,
  Button,
  DateFormField,
  Form,
  FormSubmitButton,
  SimpleSelectFormField,
  TextAreaFormField,
  TextFormField,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  useCreateMatterPropertyDamage,
  useDeleteMatterPropertyDamage,
  useUpdateMatterPropertyDamage,
} from "@/lib/data/react-query/matters/matter-property-damage";
import { MatterPropertyDamageSchema } from "@/lib/data/schemas/matters/MatterSchema";
import {
  MATTER_EXPENSE_PAID_BY_METHOD_OPTIONS,
  MATTER_EXPENSE_PAID_STATUS_OPTIONS,
} from "@/lib/data/static/matters/expenseStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  SLIDEOVER_DESCRIPTION,
  SLIDEOVER_TITLE,
} from "@/lib/data/static/slideoverStatics";
import type {
  MatterPropertyDamage,
  MatterSlideoverInitialTabsType,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { mergeInitialData } from "@/lib/utils/initialDataUtils";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { useModals } from "@/providers/ModalProvider";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";

interface AddOrEditPropertyDamageSlideoverProps {
  title: string;
  description?: string;
  editing: boolean;
  initialTab?: MatterSlideoverInitialTabsType;
  matterId: string;
  open: boolean;
  propertyDamageId?: string;
  setEditing?: (editing: boolean) => void;
  setOpen: (open: boolean) => void;
  setSelectedPropertyDamage?: (propertyDamage: MatterPropertyDamage) => void;
  initialData?: Partial<MatterPropertyDamage>;
}

const propertyDamageFormSchema = MatterPropertyDamageSchema.omit({
  connections: true,
  connectionsNextCursor: true,
  dateCreated: true,
  dateModified: true,
  firmId: true,
  id: true,
  matterId: true,
});

const defaultFormValues = {
  label: "",
  originalAmount: null,
  paidAmount: null,
  description: undefined,
  paymentDate: undefined,
  paymentMethod: undefined,
  paymentMethodDetails: undefined,
  paymentStatus: undefined,
};

export const AddOrEditPropertyDamageSlideover = ({
  editing,
  title,
  description,
  matterId,
  open,
  propertyDamageId,
  setOpen,
  initialData,
}: AddOrEditPropertyDamageSlideoverProps) => {
  const { updateSlideoverPayload } = useModals();
  const [isDeleting, setIsDeleting] = useState(false);
  const { hasActionPermission } = useUser();
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;
  const propertyDamage = matter.propertyDamages.find(
    (propertyDamage) => propertyDamage.id === propertyDamageId,
  );
  const propertyDamageInitialValues = mergeInitialData(
    propertyDamage,
    initialData,
  );
  const propertyDamageForm = useForm<z.infer<typeof propertyDamageFormSchema>>({
    resolver: zodResolver(propertyDamageFormSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...(propertyDamageInitialValues ?? {}),
        }
      : defaultFormValues,

    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const createPropertyDamage = useCreateMatterPropertyDamage();
  const updatePropertyDamage = useUpdateMatterPropertyDamage();
  const deletePropertyDamage = useDeleteMatterPropertyDamage();
  const isFormLoading =
    createPropertyDamage.isPending || updatePropertyDamage.isPending;
  const [closeAfterSave, setCloseAfterSave] = useState(false);
  const propertyDamageModalFormId = "add-or-edit-property-damage-modal-form";

  const onSubmit = async (data: z.infer<typeof propertyDamageFormSchema>) => {
    let resData;
    if (editing && propertyDamage) {
      resData = await updatePropertyDamage.mutateAsync({
        ...data,
        matterId: matter.id,
        dateModified: propertyDamage.dateModified,
        id: propertyDamage.id,
      });
    } else {
      resData = await createPropertyDamage.mutateAsync({
        matterId: matter.id,
        ...data,
      });
      updateSlideoverPayload("property-damage", {
        editing: true,
        propertyDamageId: resData.data.id,
        title: SLIDEOVER_TITLE.PROPERTY_DAMAGE.UPDATE,
        description: SLIDEOVER_DESCRIPTION.PROPERTY_DAMAGE.UPDATE,
      });
    }
    if (closeAfterSave) {
      setOpen(false);
    }
  };

  const propertyDamageOriginalAmountFormValue =
    propertyDamageForm.watch("originalAmount");
  const propertyDamagePaidStatusFormValue =
    propertyDamageForm.watch("paymentStatus");
  const propertyDamagePaidAmountFormValue =
    propertyDamageForm.watch("paidAmount");

  useEffect(() => {
    if (propertyDamagePaidStatusFormValue === "Paid") {
      propertyDamageForm.setValue(
        "paidAmount",
        propertyDamageForm.getValues("originalAmount"),
      );
    } else if (propertyDamageForm.getValues("paymentStatus") === "Unpaid") {
      propertyDamageForm.setValue("paidAmount", 0);
    }
  }, [
    propertyDamagePaidStatusFormValue,
    propertyDamageOriginalAmountFormValue,
    propertyDamagePaidAmountFormValue,
    propertyDamageForm,
  ]);

  useEffect(() => {
    if (!open) {
      propertyDamageForm.reset();
    }
  }, [open, propertyDamageForm]);

  const modalFooter = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="min-w-0">
        {editing &&
          propertyDamage &&
          hasActionPermission(
            ACTION_PERMISSION_KEYS.matterPropertyDamage,
            ACTION_PERMISSION_VALUES.delete,
          ) && (
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                setIsDeleting(true);
              }}
            >
              Delete
            </Button>
          )}
      </div>
      <div className="flex flex-wrap justify-end gap-2">
        <FormCancelButton
          setOpen={setOpen}
          isDirty={propertyDamageForm.formState.isDirty}
        />
        <FormSubmitButton
          variant="light-primary"
          loading={isFormLoading}
          overrideDisabled={Boolean(initialData)}
          submitFn={() => {
            setCloseAfterSave(false);
          }}
          title="Save"
          submitFormId={propertyDamageModalFormId}
        />
        <FormSubmitButton
          variant="default"
          loading={isFormLoading}
          overrideDisabled={Boolean(initialData)}
          submitFn={() => {
            setCloseAfterSave(true);
          }}
          title="Save and close"
          submitFormId={propertyDamageModalFormId}
        />
      </div>
    </div>
  );

  return (
    <>
      <Form {...propertyDamageForm}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={title}
          description={description}
          size="2xl"
          isDirty={propertyDamageForm.formState.isDirty}
          loading={isFormLoading}
          footer={modalFooter}
        >
          <form
            id={propertyDamageModalFormId}
            className="relative flex min-h-0 flex-1 flex-col"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await propertyDamageForm.handleSubmit(onSubmit)();
            }}
          >
            {isFormLoading && <FormLoadingOverlay />}
            <div className="overflow-y-auto">
              <fieldset
                className={cn(
                  slideoverFieldsetClassName,
                  "my-0 grid-cols-1 p-4 sm:p-5 md:grid-cols-2",
                )}
                disabled={
                  editing
                    ? !hasActionPermission(
                        ACTION_PERMISSION_KEYS.matterPropertyDamage,
                        ACTION_PERMISSION_VALUES.update,
                      )
                    : false
                }
              >
                <TextFormField
                  register={propertyDamageForm.register("label")}
                  label="Label*"
                />
                <div></div>
                <CurrencyFormField
                  register={propertyDamageForm.register("originalAmount")}
                  label="Original amount"
                />

                <DateFormField
                  register={propertyDamageForm.register("paymentDate")}
                  name="paymentDate"
                  label="Payment date"
                />
                <SimpleSelectFormField
                  register={propertyDamageForm.register("paymentMethod")}
                  label="Payment method"
                  items={MATTER_EXPENSE_PAID_BY_METHOD_OPTIONS}
                  placeholder="Select payment method"
                  clearable
                />
                <TextFormField
                  register={propertyDamageForm.register("paymentMethodDetails")}
                  label="Payment method details"
                />
                <SimpleSelectFormField
                  register={propertyDamageForm.register("paymentStatus")}
                  label="Payment status"
                  items={MATTER_EXPENSE_PAID_STATUS_OPTIONS}
                  placeholder="Select payment status"
                  clearable
                />
                <CurrencyFormField
                  register={propertyDamageForm.register("paidAmount")}
                  label="Paid amount"
                  disabled={
                    propertyDamagePaidStatusFormValue === "Paid" ||
                    propertyDamagePaidStatusFormValue === "Unpaid"
                  }
                />
                <div className="col-span-2">
                  <TextAreaFormField
                    register={propertyDamageForm.register("description")}
                    label="Description"
                  />
                </div>
              </fieldset>
            </div>
          </form>
        </AppModal>
      </Form>

      {editing && propertyDamage && (
        <ActionConfirmationModal
          open={isDeleting}
          setOpen={setIsDeleting}
          title="Are you sure you want to delete this property damage?"
          description="This action cannot be undone. This will remove the property damage and all associated tasks and files."
          afterActionFn={() => {
            deletePropertyDamage.mutate({
              matterId: matter.id,
              id: propertyDamage.id,
            });
            setOpen(false);
          }}
        />
      )}

      {/* {providerContactId && (
        <EditContactModal
          activeContactId={providerContactId}
          open={isEditingContact}
          setOpen={setIsEditingContact}
          hasEditPermission={hasActionPermission(
            ACTION_PERMISSION_KEYS.contact,
            ACTION_PERMISSION_VALUES.update,
          )}
        />
      )} */}
    </>
  );
};
